import { objectWithoutProperties as _objectWithoutProperties, slicedToArray as _slicedToArray, extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React__default, { useRef } from 'react';
import '@walmart-web/livingdesign-components/index.esm.css';
import { Menu, Body, MenuItem } from '@walmart-web/livingdesign-components';
import '../Alert/Alert.js';
import '../Button/Button.js';
import '../Button/ButtonGroup.js';
import '../Radio/Radio.js';
import '../Tag/Tag.js';
import '../Checkbox/Checkbox.js';
import { Chip } from '../Chip/Chip.js';
import classNames from '../../node_modules/classnames/index.js';
import '../IconButton/IconButton.js';
import { FormHelperText } from '../Form/FormHelperText.js';
import { FormLabel } from '../Form/FormLabel.js';
import '../TextField/TextField.js';
import '../TabNavigation/TabNavigation.js';
import '../TabNavigation/TabNavigationItem.js';
import '../Modal/Modal.js';
import '@livingdesign/tokens/dist/js/light/regular/components/ErrorMessage';
import '../SearchBox/SearchBox.js';
import '../AutoComplete/AutoComplete.js';
import '../TextArea/TextArea.js';
import '../ProgressIndicator/ProgressIndicator.js';
import '../Panel/Panel.js';
import '../Calendar/context.js';
import 'dayjs';
import '../Calendar/RenderDays.js';
import '../Calendar/RenderWeeks.js';
import { CaretDown, Check } from '@livingdesign/icons';
import '../Calendar/locale/wmLocale.js';
import '../Calendar/locale/stdLocale.js';
import '../utility/constants.js';
import styles from './LuminateSelect.module.scss.js';
import { useSSRSafeId } from '@react-aria/ssr';
import '../CardActionArea/CardActionArea.js';
import '../HierarchySelector/HierarchySelector.js';
import '../Pagination/Pagination.js';
import '../DisplayTag/DisplayTag.js';
import '../PageHeader/PageHeader.js';
import '../Table/Table.js';
import '../Tooltip/Tooltip.js';
import '../ValueChange/ValueChange.js';

var _excluded = ["className", "label", "placeholder", "leadingIcon", "options", "onChange", "disabled", "helperText", "error", "size", "value", "horizontalLabel", "isFullWidth", "menuTriggerRef", "onBlur", "maxMenuHeight", "scrollableDivId"];
/**
 * Select is a component that allows users to select an option from a list.
 * It is a custom component built with a combination of the `Menu`, `ListItem` and `MenuItem` Living Design components.
 *
 *
 * ### Content
 *
 * Select is a controlled component i.e., the state is controlled by the `value` prop and `onChange` callback passed from the parent component.
 *
 * Select accepts the following props:
 *  - `options`: List of items for the Select field. This can either be an array of options OR an array of objects with a `key` and `label`
 *  - `onBlur`: The callback executed when the select field is blurred. Since this is a custom component, the onBlur callback is called when the menu is closed (Optional)
 *  - `onChange`: Callback to send the selected option
 *  - `label`: The label for the Select field. (Optional)
 *  - `placeholder`: The placeholder for the Select field. (Optional)
 *  - `leadingIcon`: The leading content for the list item. (Optional)
 *  - `disabled`: Flag to disable the Select field. (Optional)
 *  - `helperText`: The helper text for the Select field.  (Optional)
 *  - `error`: The error for the Select field. (Optional)
 *  - `size`: The size of the list item. (Optional)
 *  - `value`: The value for the component. Passing value will also preselect the option. (Optional)
 *  - `menuTriggerRef`: The ref for the select menu (Optional)
 *  - `maxMenuHeight`: The maximum height for the menu (Optional)
 *
 * ### Usage
 *
 * ```tsx
 *
 * import { Select } from '@walmart-dataventures/shared-components';
 *
 * const options = ['January', 'February', 'March'];
 *
 * <Select
 *   label="Select an option"
 *   options={options}
 *   onChange={() => console.log}
 * />
 *
 * ```
 *
 * OR
 *
 * ```tsx
 *
 * import { Select } from '@walmart-dataventures/shared-components';
 *
 * const options = [
 *  { key: 'jan', label: 'January' }
 *  { key: 'feb', label: 'February' }
 *  { key: 'mar', label: 'March' }
 * ];
 *
 * <Select
 *   label="Select an option"
 *   options={options}
 *   onChange={() => console.log}
 * />
 *
 * ```
 **/

var Select = function Select(_ref) {
  var className = _ref.className,
    label = _ref.label,
    placeholder = _ref.placeholder,
    leadingIcon = _ref.leadingIcon,
    options = _ref.options,
    onChange = _ref.onChange,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    helperText = _ref.helperText,
    error = _ref.error,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'large' : _ref$size,
    value = _ref.value,
    _ref$horizontalLabel = _ref.horizontalLabel,
    horizontalLabel = _ref$horizontalLabel === void 0 ? false : _ref$horizontalLabel,
    _ref$isFullWidth = _ref.isFullWidth,
    isFullWidth = _ref$isFullWidth === void 0 ? false : _ref$isFullWidth,
    menuTriggerRef = _ref.menuTriggerRef,
    onBlur = _ref.onBlur,
    maxMenuHeight = _ref.maxMenuHeight,
    _ref$scrollableDivId = _ref.scrollableDivId,
    scrollableDivId = _ref$scrollableDivId === void 0 ? 'menuListScrollableDiv' : _ref$scrollableDivId,
    rest = _objectWithoutProperties(_ref, _excluded);
  var _React$useState = React__default.useState(false),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    isOpen = _React$useState2[0],
    setIsOpen = _React$useState2[1];
  var helperId = useSSRSafeId();
  var labelId = useSSRSafeId();
  var handleOnClose = function handleOnClose(event) {
    var target = event.target;
    if (target instanceof HTMLElement) {
      if (target.nodeName === 'INPUT') {
        setIsOpen(true);
      } else {
        setIsOpen(false);
      }
    }
  };

  /**
   * Returns the key of an option.
   *
   * @param {string | OptionTypeWithKeyValue} option - The option to retrieve the key from.
   * @return {string} The key of the option. If the option is a string, it is returned as is.
   *                  If the option is an object with a `key` property, the value of that property is returned.
   */
  var getOptionKey = function getOptionKey(option) {
    return typeof option === 'string' ? option : String(option.key);
  };

  /**
   * Returns the label of an option.
   *
   * @param {string | OptionTypeWithKeyValue} option - The option to retrieve the label from.
   * @return {string} The label of the option. If the option is a string, it is returned as is.
   *                  If the option is an object with a `label` property, the value of that property is returned.
   */
  var getOptionLabel = function getOptionLabel(option) {
    return typeof option === 'string' ? option : String(option.label);
  };

  /**
   * Retrieves the label associated with a given value from the options array.
   *
   * If the options array contains strings, the label is returned as is.
   * If the options array contains objects with a `key` property, the label is retrieved from the `key` property.
   *
   * @param {string} value - The value to search for in the options array.
   * @return {string | undefined} The label associated with the given value, or undefined if not found.
   */
  var getLabelFromOptions = function getLabelFromOptions(value) {
    if (typeof options[0] === 'string') {
      return value;
    } else {
      var _find;
      return (_find = options.find(function (option) {
        return option.key === value;
      })) === null || _find === void 0 ? void 0 : _find.label;
    }
  };
  var helperContent = error !== null && error !== void 0 ? error : helperText;
  var isPlaceholder = !!!value && !!placeholder;
  return /*#__PURE__*/React__default.createElement("div", {
    className: classNames(className, styles.container, label ? styles.labelAndFieldGap : '', label && horizontalLabel ? styles.horizontalLabel : '')
  }, label ? /*#__PURE__*/React__default.createElement(FormLabel, {
    htmlFor: labelId,
    className: styles.label
  }, label) : null, /*#__PURE__*/React__default.createElement(Menu, _extends({
    isOpen: isOpen,
    onClose: function onClose(e) {
      handleOnClose(e);
      onBlur === null || onBlur === void 0 || onBlur();
    },
    onOpen: function onOpen() {
      return setIsOpen(true);
    },
    trigger: /*#__PURE__*/React__default.createElement(Chip, {
      ref: menuTriggerRef || useRef(null),
      UNSAFE_className: classNames(disabled && styles.disabled, error && styles.error, isFullWidth && styles.fullWidth, styles.chipOverrides),
      leading: leadingIcon,
      size: size,
      trailing: /*#__PURE__*/React__default.createElement(CaretDown, {
        size: "small"
      }),
      selected: !!value,
      "aria-pressed": undefined // To supress the aria-pressed property injected with-in Chip Component.
    }, /*#__PURE__*/React__default.createElement(Body, {
      as: "p",
      size: "small",
      weight: 400,
      UNSAFE_className: classNames(value && styles.displayText, isPlaceholder && styles.placeholder)
    }, value ? getLabelFromOptions(value) : placeholder)),
    triggerRef: menuTriggerRef || useRef(null),
    position: "bottomLeft",
    id: labelId
  }, rest), /*#__PURE__*/React__default.createElement("div", {
    id: scrollableDivId,
    key: "menu",
    style: {
      maxHeight: maxMenuHeight !== null && maxMenuHeight !== void 0 ? maxMenuHeight : window.innerHeight,
      overflowY: maxMenuHeight !== undefined ? 'scroll' : 'auto'
    },
    "data-testid": "menu"
  }, options === null || options === void 0 ? void 0 : options.map(function (option, index) {
    return /*#__PURE__*/React__default.createElement(MenuItem, {
      key: index,
      UNSAFE_className: styles.menuItem,
      onClick: function onClick() {
        return onChange(getOptionKey(option));
      }
    }, /*#__PURE__*/React__default.createElement(Body, {
      size: "small",
      weight: 400,
      UNSAFE_className: classNames(styles.select, value === option ? styles.active : '')
    }, getOptionLabel(option), value === getOptionKey(option) ? /*#__PURE__*/React__default.createElement(Check, null) : null));
  }))), !!helperContent && /*#__PURE__*/React__default.createElement(FormHelperText, {
    UNSAFE_className: styles.helperText,
    disabled: disabled,
    id: helperId,
    hasError: !!error
  }, helperContent));
};
var LuminateSelect = function LuminateSelect(props) {
  if (process.env.NODE_ENV === 'development') {
    console.warn('LuminateSelect is deprecated. Please use Select instead.');
  }
  return /*#__PURE__*/React__default.createElement(Select, props);
};

export { LuminateSelect, Select };
